
import React, { useEffect } from "react";
import {
  Mail, Phone, MapPin, ArrowUpRight, GraduationCap,
  FileCheck, ClipboardList, CalendarDays, BookOpen,
  Award, Target, Eye, Lightbulb, ShieldCheck,
  Users, Sparkles, CheckCircle2, Clock, FileText,
  ChevronRight, Headset, UserCheck, Building2
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" }
  }
};

const officers = [
  {
    id: 1,
    name: "Sayed Tanjimul Hakim",
    designation: "Admission Officer",
    image: "/assets/img/tanjim.jpeg",
    phone: "01400622928",
    email: "sthsafwan007@gmail.com",
    featured: true
  },
  {
    id: 2,
    name: "Mohammad Bashir Ullah (Saimum)",
    designation: "Admission Officer",
    image: "/assets/img/bashir.jpeg",
    phone: "01865758101",
    email: "bashirullahsaimum@gmail.com",
    featured: false
  }
];

const services = [
  {
    icon: ClipboardList,
    title: "Admission Information",
    description: "Providing accurate information about admission requirements, eligibility and procedures."
  },
  {
    icon: FileCheck,
    title: "Application Assistance",
    description: "Supporting prospective students throughout the university application process."
  },
  {
    icon: CalendarDays,
    title: "Admission Schedule",
    description: "Information about admission deadlines, important dates and admission activities."
  },
  {
    icon: GraduationCap,
    title: "Program Information",
    description: "Guidance about academic programs, departments and available study opportunities."
  },
  {
    icon: Users,
    title: "Student Counseling",
    description: "Helping prospective students make informed decisions about their academic future."
  },
  {
    icon: Headset,
    title: "Admission Support",
    description: "Responding to admission inquiries and providing assistance to applicants."
  }
];

const principles = [
  {
    icon: Eye,
    title: "Our Vision",
    color: "bg-blue-600",
    description: "To become a leading institution of higher learning in Asia."
  },
  {
    icon: Target,
    title: "Our Mission",
    color: "bg-emerald-600",
    description: "To nurture creativity, research, and leadership through quality education."
  },
  {
    icon: Lightbulb,
    title: "Our Goals",
    color: "bg-orange-500",
    description: "Expanding research opportunities and strengthening global partnerships."
  }
];

export default function AdmDiv({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Admission and Information Division | Bandarban University"
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="bg-[#f6f9f7] text-gray-800 overflow-hidden">

      {/* HERO SECTION */}
      <section className="relative min-h-[490px] bg-[#073b2a] text-white overflow-hidden flex items-center">

        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(#a7f3d0 1px, transparent 1px)",
            backgroundSize: "28px 28px"
          }}
        />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute -right-36 -top-48 w-[600px] h-[600px] rounded-full border border-white/10"
        />

        <div className="absolute right-0 bottom-0 w-96 h-96 bg-emerald-400/20 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-2 gap-12 items-center">

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-emerald-200 text-sm mb-7">
              <Sparkles size={16} />
              Bandarban University
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              Your Journey
              <span className="block text-emerald-300">
                Starts Here
              </span>
            </h1>

            <h2 className="text-xl md:text-2xl font-semibold mt-4 text-white/90">
              Admission and Information Division
            </h2>

            <p className="text-gray-300 text-lg leading-8 mt-6 max-w-xl">
              Discover opportunities, explore academic programs
              and take the first step towards a brighter future.
              We are here to guide you throughout your admission journey.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">
              <a
                href="#officers"
                className="px-7 py-3.5 rounded-xl bg-emerald-400 text-[#073b2a] font-bold hover:bg-white transition flex items-center gap-2"
              >
                Meet Our Team
                <ArrowUpRight size={19} />
              </a>

              <a
                href="#services"
                className="px-7 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 transition"
              >
                Explore Services
              </a>
            </div>
          </motion.div>

          {/* ANIMATED ADMISSION GRAPHIC */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative w-[360px] h-[360px] flex items-center justify-center">

              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed border-emerald-300/40"
              />

              <div className="absolute inset-8 rounded-full border border-white/10" />

              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative w-64 h-64 rounded-[40px] bg-gradient-to-br from-emerald-300 to-teal-700 shadow-2xl flex flex-col items-center justify-center"
              >
                <GraduationCap size={95} className="text-white mb-4" />
                <h3 className="text-2xl font-bold">Your Future</h3>
                <p className="text-sm text-white/80 mt-1">
                  Begins With Education
                </p>
              </motion.div>

              {[
                { Icon: BookOpen, pos: "top-2 left-8" },
                { Icon: FileCheck, pos: "top-8 right-4" },
                { Icon: Award, pos: "bottom-4 left-8" },
                { Icon: Users, pos: "bottom-5 right-5" }
              ].map(({ Icon, pos }, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.4
                  }}
                  className={`absolute ${pos} w-14 h-14 rounded-2xl bg-white text-emerald-800 shadow-xl flex items-center justify-center`}
                >
                  <Icon size={25} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHT STRIP */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {[
            {
              icon: GraduationCap,
              title: "Academic Opportunities",
              text: "Explore Your Future"
            },
            {
              icon: UserCheck,
              title: "Admission Guidance",
              text: "Friendly Student Support"
            },
            {
              icon: ShieldCheck,
              title: "Reliable Information",
              text: "Official University Guidance"
            }
          ].map((item, i) => (
            <div key={i} className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <item.icon size={24} />
              </div>
              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ADMISSION OFFICERS */}
      <section id="officers" className="py-24 px-6">

        <div className="max-w-7xl mx-auto">

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Team
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Meet Our Admission Officers
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Our dedicated team is ready to assist prospective students
              with admission information and guidance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {officers.map((person, i) => (
              <motion.div
                key={person.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.7 }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-[28px] overflow-hidden border border-gray-100 shadow-lg hover:shadow-2xl transition duration-300"
              >

                {/* PHOTO */}
                <div className="relative h-[340px] bg-gradient-to-br from-emerald-900 to-teal-700 overflow-hidden">

                  <div
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: "radial-gradient(white 1px, transparent 1px)",
                      backgroundSize: "20px 20px"
                    }}
                  />

                  <img
                    src={person.image}
                    alt={person.name}
                    className="relative w-full h-full object-cover object-top transition duration-700 group-hover:scale-105"
                  />

                  <div className="absolute top-5 left-5">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur-md text-emerald-800 text-xs font-bold shadow-lg">
                      <CheckCircle2 size={15} />
                      Admission Officer
                    </span>
                  </div>

                  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                {/* DETAILS */}
                <div className="p-7">

                  <h3 className="text-xl md:text-2xl font-extrabold text-gray-900">
                    {person.name}
                  </h3>

                  <p className="text-emerald-700 font-semibold mt-2">
                    {person.designation}
                  </p>

                  <p className="text-gray-400 text-sm mt-1">
                    Bandarban University
                  </p>

                  <div className="h-px bg-gray-100 my-6" />

                  <div className="space-y-4">

                    <a
                      href={`tel:${person.phone}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-emerald-700 transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Phone size={18} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Phone Number</p>
                        <p className="text-sm font-semibold">{person.phone}</p>
                      </div>
                    </a>

                    <a
                      href={`mailto:${person.email}`}
                      className="flex items-center gap-3 text-gray-600 hover:text-emerald-700 transition"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Mail size={18} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400">Email Address</p>
                        <p className="text-sm font-semibold break-all">
                          {person.email}
                        </p>
                      </div>
                    </a>

                  </div>

                  <a
                    href={`tel:${person.phone}`}
                    className="mt-7 w-full flex justify-center items-center gap-2 py-3.5 rounded-xl bg-[#0b4933] text-white font-bold hover:bg-emerald-600 transition"
                  >
                    Contact Officer
                    <ArrowUpRight size={18} />
                  </a>

                </div>
              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* ADMISSION SERVICES */}
      <section id="services" className="py-24 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              How We Help
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Admission Services
            </h2>

            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              We are committed to making the admission process
              informative, accessible and convenient for every applicant.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {services.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                className="group p-7 rounded-2xl border border-gray-100 bg-[#f8fbf9] hover:bg-[#0b4933] hover:text-white hover:shadow-2xl transition duration-300"
              >

                <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-white/20 group-hover:text-white transition">
                  <item.icon size={27} />
                </div>

                <h3 className="text-xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 group-hover:text-gray-200 mt-3 leading-7 text-sm">
                  {item.description}
                </p>

                <div className="mt-6 text-emerald-700 group-hover:text-emerald-300 flex items-center gap-2 text-sm font-bold">
                  Admission Support
                  <ChevronRight size={17} />
                </div>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* STUDENT CTA */}
      <section className="px-6 py-16 bg-[#f1f7f3]">

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative max-w-7xl mx-auto overflow-hidden rounded-[30px] bg-gradient-to-r from-emerald-900 via-[#0b4933] to-teal-700 p-9 md:p-14 text-white"
        >

          <div className="absolute -right-20 -top-32 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute right-20 bottom-[-150px] w-72 h-72 rounded-full bg-emerald-300/10 blur-3xl" />

          <div className="relative grid md:grid-cols-2 gap-8 items-center">

            <div>
              <span className="text-emerald-300 text-sm font-bold uppercase tracking-[3px]">
                Future Starts Today
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold mt-4 leading-tight">
                Take the First Step Towards Your Dream
              </h2>

              <p className="text-gray-200 mt-5 leading-8">
                Have questions about admission? Our Admission and
                Information Division is here to guide you.
              </p>
            </div>

            <div className="flex md:justify-end">
              <a
                href="mailto:admission@bubban.ac.bd"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-emerald-300 text-[#073b2a] font-bold hover:bg-white transition shadow-xl"
              >
                Ask About Admission
                <ArrowUpRight size={20} />
              </a>
            </div>

          </div>
        </motion.div>

      </section>

      {/* VISION MISSION GOALS */}
      <section className="py-24 px-6 bg-[#f6f9f7]">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Direction
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Vision, Mission & Goals
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-7">

            {principles.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
                className="bg-white p-8 rounded-3xl shadow-md hover:shadow-xl transition"
              >

                <div className={`w-14 h-14 ${item.color} rounded-2xl text-white flex items-center justify-center`}>
                  <item.icon size={28} />
                </div>

                <h3 className="text-2xl font-bold mt-6">
                  {item.title}
                </h3>

                <p className="text-gray-500 leading-8 mt-4">
                  {item.description}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="relative overflow-hidden bg-[#073b2a] text-white py-20 px-6">

        <div className="absolute w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl -right-20 -top-20" />

        <div className="relative max-w-7xl mx-auto">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            <div>
              <span className="text-emerald-300 font-bold uppercase tracking-[3px] text-sm">
                Get In Touch
              </span>

              <h2 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
                We're Here to Help You Begin
              </h2>

              <p className="text-gray-300 mt-5 leading-8">
                Connect with the Admission and Information Division
                for admission inquiries, program information and
                student guidance.
              </p>

              <a
                href="mailto:admission@bubban.ac.bd"
                className="inline-flex items-center gap-3 mt-8 bg-emerald-400 text-[#073b2a] font-bold px-7 py-4 rounded-xl hover:bg-white transition"
              >
                Contact Admission Office
                <ArrowUpRight size={20} />
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-7 space-y-6">

              <div className="flex gap-4 items-start">
                <MapPin className="text-emerald-300 shrink-0" size={23} />
                <div>
                  <h4 className="font-bold">University Address</h4>
                  <p className="text-gray-300 text-sm mt-2 leading-7">
                    K.B Road, Shualok, Post Office: Shualok - 4600,
                    Bandarban Sadar, Bangladesh
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Phone className="text-emerald-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Office Phone</h4>
                  <a href="tel:+8801318305900" className="text-gray-300 text-sm">
                    +880 1318-305900
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-center">
                <Mail className="text-emerald-300 shrink-0" />
                <div>
                  <h4 className="font-bold">Official Email</h4>
                  <a href="mailto:admission@bubban.ac.bd" className="text-gray-300 text-sm">
                    admission@bubban.ac.bd
                  </a>
                </div>
              </div>

              <div className="border-t border-white/20 pt-5">
                <p className="text-xs text-gray-400">
                  ADMISSION AND INFORMATION DIVISION
                </p>
                <p className="font-bold mt-1">
                  Bandarban University
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
